import { useCallback, useState, useEffect } from 'react';
import { chatService } from '@/services/chatService';
import type { ChatSession } from '@/types';

interface UseChatSessionsOptions {
  limit?: number;
  autoLoad?: boolean;
}

interface UseChatSessionsReturn {
  sessions: ChatSession[];
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  search: string;
  setSearch: (search: string) => void;
  loadPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  createSession: (title?: string) => Promise<ChatSession>;
}

export function useChatSessions(options: UseChatSessionsOptions = {}): UseChatSessionsReturn {
  const { limit = 10, autoLoad = true } = options;

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');

  const loadPage = useCallback(async (page: number) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await chatService.getGlobalSessions({
        page,
        limit,
        search: search || undefined,
      });

      setSessions(response.data);
      setCurrentPage(response.meta.currentPage);
      setTotalPages(response.meta.totalPages);
      setTotalCount(response.meta.totalCount);
    } catch (err) {
      console.error('Failed to load chat sessions:', err);
      setError('채팅 목록을 불러올 수 없습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [limit, search]);

  const refresh = useCallback(async () => {
    await loadPage(currentPage);
  }, [loadPage, currentPage]);

  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      await chatService.deleteGlobalSession(sessionId);
      // Remove from local state
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      setTotalCount(prev => prev - 1);
    } catch (err) {
      console.error('Failed to delete session:', err);
      throw err;
    }
  }, []);

  const createSession = useCallback(async (title?: string) => {
    try {
      const session = await chatService.createGlobalSession(title);
      // Refresh to show new session at top
      await loadPage(1);
      return session;
    } catch (err) {
      console.error('Failed to create session:', err);
      throw err;
    }
  }, [loadPage]);

  // Auto load on mount and when search changes
  useEffect(() => {
    if (autoLoad) {
      loadPage(1);
    }
  }, [autoLoad, search]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    sessions,
    isLoading,
    error,
    currentPage,
    totalPages,
    totalCount,
    search,
    setSearch,
    loadPage,
    refresh,
    deleteSession,
    createSession,
  };
}

// 채팅 세션 생성 이벤트 (사이드바 새로고침용)
export const CHAT_SESSION_CREATED_EVENT = 'chatSessionCreated';
// 채팅 세션 제목 업데이트 이벤트 (첫 메시지 전송 후)
export const CHAT_SESSION_UPDATED_EVENT = 'chatSessionUpdated';

export function dispatchChatSessionCreated() {
  window.dispatchEvent(new CustomEvent(CHAT_SESSION_CREATED_EVENT));
}

export function dispatchChatSessionUpdated() {
  window.dispatchEvent(new CustomEvent(CHAT_SESSION_UPDATED_EVENT));
}

// Simple hook for recent sessions in sidebar (no pagination needed)
export function useRecentChatSessions(limit: number = 5) {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await chatService.getGlobalSessions({ limit });
      setSessions(response.data);
    } catch (err) {
      console.error('Failed to load recent sessions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // 세션 생성/업데이트 이벤트 리스닝 (자동 새로고침)
  useEffect(() => {
    const handleSessionChange = () => {
      loadSessions();
    };

    window.addEventListener(CHAT_SESSION_CREATED_EVENT, handleSessionChange);
    window.addEventListener(CHAT_SESSION_UPDATED_EVENT, handleSessionChange);
    return () => {
      window.removeEventListener(CHAT_SESSION_CREATED_EVENT, handleSessionChange);
      window.removeEventListener(CHAT_SESSION_UPDATED_EVENT, handleSessionChange);
    };
  }, [loadSessions]);

  return {
    sessions,
    isLoading,
    refresh: loadSessions,
  };
}
